<template>
  <div class="shipping-dashboard">
    <!-- ============ 上半部分：库存预警 + 用户下单统计 ============ -->
    <div class="top-row">
      <!-- 库存预警 -->
      <section class="panel">
        <div class="panel-header">
          <div class="panel-title">
            <i class="title-dot dot-warning"></i>
            <span>商品下架预警</span>
          </div>
          <el-select v-model="goodsType" size="mini" style="width: 100px" placeholder="请选择" @change="fetchData">
            <el-option label="本月" :value="1" />
            <el-option label="本周" :value="2" />
          </el-select>
        </div>

        <div class="panel-body panel-body--bordered">
          <table class="data-table">
            <thead>
              <tr>
                <th>商品名称</th>
                <th style="width: 165px">下架时间</th>
                <th style="width: 76px">状态</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(item, index) in stockWarning" :key="'stock-' + index" @click="goGoodsList(item)"
                style="cursor: pointer">
                <td class="name-cell" :title="item.name">{{ item.name }}</td>
                <td class="time-cell">{{ item.removal_time }}</td>
                <td>
                  <el-tag type="danger" size="mini" class="blink-tag">紧急</el-tag>
                </td>
              </tr>
              <tr v-if="stockWarning.length === 0">
                <td colspan="3" class="empty-cell">暂无数据</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      <!-- 用户下单统计 -->
      <section class="panel">
        <div class="panel-header">
          <div class="panel-title">
            <i class="title-dot dot-order"></i>
            <span>用户下单统计</span>
          </div>
          <div class="panel-tools">
            <el-select v-model="kor_type" size="mini" style="width: 130px" filterable placeholder="选择线路"
              @change="queryData">
              <el-option label="韩到中线路" :value="0" />
              <el-option label="韩到日线路" :value="3" />
              <el-option label="韩到马来线路" :value="4" />
              <el-option label="中到马来线路" :value="5" />
            </el-select>
            <el-date-picker v-model="selectedMonth" type="month" size="mini" style="width: 128px" placeholder="选择年月"
              format="yyyy年MM月" value-format="yyyy-MM" :clearable="false" :editable="false"
              @change="handleMonthChange"></el-date-picker>
            <button class="export-btn" @click="exportData">
              <i class="export-icon"></i>
              导出
            </button>
          </div>
        </div>

        <div class="panel-body panel-body--bordered">
          <table class="data-table">
            <thead>
              <tr>
                <th>用户</th>
                <th style="width: 110px">总下单数</th>
                <th style="width: 120px">本月下单数</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(item, index) in userList" :key="'user-' + index">
                <td class="user-cell">
                  {{ item?.user?.nickname || '-' }} {{ item?.user?.ydm_code }}
                  {{ item?.user?.code }}
                </td>
                <td>{{ item.num || item.totalOrders || 0 }}</td>
                <td class="month-orders">
                  {{ item.month_num || item.monthOrders || 0 }}
                </td>
              </tr>
              <tr v-if="userList.length === 0">
                <td colspan="3" class="empty-cell">暂无数据</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="panel-footer">
          <span class="note-text">
            *注：首页仅展示前20位用户数据，请导出查看全部用户数据
          </span>
        </div>
      </section>
    </div>

    <!-- ============ 下半部分：销量 + 点击量排行榜 ============ -->
    <div class="rank-row">
      <!-- 商品销量排行榜 -->
      <section class="panel">
        <div class="panel-header">
          <div class="panel-title">
            <i class="title-dot dot-sales"></i>
            <span>商品销量排行榜</span>
            <span class="rank-subtitle">TOP {{ goodsSales.length }}</span>
          </div>
          <el-date-picker v-model="salesMonth" type="month" size="mini" style="width: 128px" placeholder="选择月份"
            format="yyyy年MM月" value-format="yyyy-MM" :clearable="false" :editable="false"
            @change="handleSalesMonthChange"></el-date-picker>
        </div>

        <div v-loading="salesLoading" class="panel-body rank-list">
          <div v-for="(item, index) in goodsSales" :key="item.gid" class="rank-item">
            <div class="rank-index" :class="'rank-' + (index + 1)">
              {{ index + 1 }}
            </div>
            <img class="rank-img" :src="item.goods_pic" :alt="item.goods_name" @error="handleGoodsImgError" />
            <div class="rank-info">
              <div class="rank-name" :title="item.goods_name">
                {{ item.goods_name }}
              </div>
              <div class="rank-bar">
                <div class="rank-bar-inner" :style="{ width: getBarWidth(item.total_num) }"></div>
              </div>
            </div>
            <div class="rank-sales">
              {{ item.total_num }}<span class="rank-unit">件</span>
            </div>
          </div>

          <div v-if="!salesLoading && goodsSales.length === 0" class="empty-cell">
            暂无数据
          </div>
        </div>
      </section>

      <!-- 商品点击量排行榜 -->
      <section class="panel">
        <div class="panel-header">
          <div class="panel-title">
            <i class="title-dot dot-click"></i>
            <span>商品点击量排行榜</span>
            <span class="rank-subtitle">TOP {{ clickGoods.length }}</span>
          </div>
          <el-date-picker v-model="clickMonth" type="month" size="mini" style="width: 128px" placeholder="选择月份"
            format="yyyy年MM月" value-format="yyyy-MM" :clearable="false" :editable="false"
            @change="handleClickMonthChange"></el-date-picker>
        </div>

        <div v-loading="clickLoading" class="panel-body rank-list">
          <div v-for="(item, index) in clickGoods" :key="index" class="rank-item">
            <div class="rank-index" :class="'rank-' + (index + 1)">
              {{ index + 1 }}
            </div>
            <img class="rank-img" :src="getFirstPic(item.pic)" :alt="item.name" @error="handleGoodsImgError" />
            <div class="rank-info">
              <div class="rank-name" :title="item.name">
                {{ item.name }}
              </div>
              <div class="rank-bar">
                <div class="rank-bar-inner rank-bar-click" :style="{ width: getClickBarWidth(item.num) }"></div>
              </div>
            </div>
            <div class="rank-sales rank-sales-click">
              {{ item.num }}<span class="rank-unit">次</span>
            </div>
          </div>

          <div v-if="!clickLoading && clickGoods.length === 0" class="empty-cell">
            暂无数据
          </div>
        </div>
      </section>
    </div>
  </div>
</template>

<script>
import {
  OrderStatistics,
  month_order_user_excel,
  GoodsWarning,
  GoodsSales,
  ClickGoodsRanking
} from '@/api/xgcApi'
import { getUploadUrl } from '@/config'

export default {
  name: 'ShippingDataTable',

  data() {
    return {
      goodsType: 1,
      user_code: '',
      kor_type: 0,
      selectedMonth: '',
      startTime: '',
      endTime: '',
      userList: [],
      allUsers: [],
      stockWarning: [],

      // 商品销量排行榜
      goodsSales: [],
      salesLoading: false,
      salesMonth: '',
      salesStartTime: '',
      salesEndTime: '',

      // 商品点击量排行榜
      clickGoods: [],
      clickLoading: false,
      clickMonth: '',
      clickStartTime: '',
      clickEndTime: ''
    }
  },

  computed: {
    salesMax() {
      return (
        this.goodsSales.reduce(
          (max, item) => Math.max(max, Number(item.total_num) || 0),
          0
        ) || 1
      )
    },
    clickMax() {
      return (
        this.clickGoods.reduce(
          (max, item) => Math.max(max, Number(item.num) || 0),
          0
        ) || 1
      )
    }
  },

  mounted() {
    const now = new Date()
    const currentYear = now.getFullYear()
    const currentMonth = now.getMonth() + 1
    const defaultMonth = `${currentYear}-${String(currentMonth).padStart(2, '0')}`

    this.selectedMonth = defaultMonth
    this.calculateMonthRange(this.selectedMonth)

    this.salesMonth = defaultMonth
    const salesRange = this.getMonthRange(defaultMonth)
    this.salesStartTime = salesRange.startTime
    this.salesEndTime = salesRange.endTime

    this.clickMonth = defaultMonth
    const clickRange = this.getMonthRange(defaultMonth)
    this.clickStartTime = clickRange.startTime
    this.clickEndTime = clickRange.endTime

    this.queryData()
    this.fetchData()
    this.fetchGoodsSales()
    this.fetchClickGoods()
  },

  methods: {
    goGoodsList(item) {
      this.$router.push({
        path: '/goodManage/regulate/manageGoods/index',
        query: {
          goods_name: item.name || '',
        },
      })
    },
    formatDateTime(date) {
      const y = date.getFullYear()
      const m = String(date.getMonth() + 1).padStart(2, '0')
      const d = String(date.getDate()).padStart(2, '0')
      const h = String(date.getHours()).padStart(2, '0')
      const min = String(date.getMinutes()).padStart(2, '0')
      const s = String(date.getSeconds()).padStart(2, '0')
      return `${y}-${m}-${d} ${h}:${min}:${s}`
    },

    getMonthRange(yearMonth) {
      if (!yearMonth) return { startTime: '', endTime: '' }
      const [year, month] = yearMonth.split('-').map(Number)
      return {
        startTime: this.formatDateTime(new Date(year, month - 1, 1, 0, 0, 0)),
        endTime: this.formatDateTime(new Date(year, month, 1, 0, 0, 0))
      }
    },

    async exportData() {
      const params = {
        start_time: this.startTime,
        end_time: this.endTime,
        kor_type: this.kor_type
      }
      const { data } = await month_order_user_excel(params)
      const fileUrl = getUploadUrl + data
      window.open(fileUrl)
      this.$message({
        type: 'success',
        message: '导出成功'
      })
    },

    calculateMonthRange(yearMonth) {
      if (!yearMonth) return null
      const range = this.getMonthRange(yearMonth)
      this.startTime = range.startTime
      this.endTime = range.endTime
      return range
    },

    handleMonthChange(value) {
      if (value) {
        this.selectedMonth = value
        this.calculateMonthRange(value)
        this.queryData()
      }
    },

    queryData() {
      const data = {
        user_code: this.user_code,
        start_time: this.startTime,
        end_time: this.endTime,
        kor_type: this.kor_type
      }

      OrderStatistics(data)
        .then((res) => {
          this.userList = res.data || []
        })
        .catch((err) => {
          console.error('查询失败:', err)
          this.$message.error('数据加载失败')
          this.userList = []
        })
    },

    async fetchData() {
      const { data } = await GoodsWarning({})
      if (this.goodsType == 1) {
        this.stockWarning = data.goods_month || []
      } else {
        this.stockWarning = data.goods_week || []
      }
    },

    handleSalesMonthChange(value) {
      const range = this.getMonthRange(value)
      this.salesStartTime = range.startTime
      this.salesEndTime = range.endTime
      this.fetchGoodsSales()
    },

    async fetchGoodsSales() {
      this.salesLoading = true
      try {
        const { data } = await GoodsSales({
          start_time: this.salesStartTime,
          end_time: this.salesEndTime
        })
        const list = Array.isArray(data) ? data : []
        this.goodsSales = list
        // .slice()
        // .sort((a, b) => Number(b.total_num || 0) - Number(a.total_num || 0))
        // .slice(0, 10)
      } catch (err) {
        console.error('商品销量加载失败:', err)
        this.goodsSales = []
      } finally {
        this.salesLoading = false
      }
    },

    handleClickMonthChange(value) {
      const range = this.getMonthRange(value)
      this.clickStartTime = range.startTime
      this.clickEndTime = range.endTime
      this.fetchClickGoods()
    },

    async fetchClickGoods() {
      this.clickLoading = true
      try {
        const { data } = await ClickGoodsRanking({
          start_time: this.clickStartTime,
          end_time: this.clickEndTime
        })
        const list = Array.isArray(data) ? data : []
        this.clickGoods = list
        // .slice()
        // .sort((a, b) => Number(b.num || 0) - Number(a.num || 0))
        // .slice(0, 10)
      } catch (err) {
        console.error('商品点击量加载失败:', err)
        this.clickGoods = []
      } finally {
        this.clickLoading = false
      }
    },

    getBarWidth(num) {
      const percent = (Number(num) / this.salesMax) * 100
      return Math.max(percent, 4) + '%'
    },

    getClickBarWidth(num) {
      const percent = (Number(num) / this.clickMax) * 100
      return Math.max(percent, 4) + '%'
    },

    getFirstPic(pic) {
      if (!pic) return ''
      return String(pic).split(',')[0].trim()
    },

    handleGoodsImgError(e) {
      e.target.src =
        "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='44' height='44'%3E%3Crect width='44' height='44' fill='%23f0f2f5'/%3E%3Ctext x='50%25' y='50%25' font-size='10' fill='%23c0c4cc' text-anchor='middle' dominant-baseline='middle'%3E无图%3C/text%3E%3C/svg%3E"
    }
  }
}
</script>

<style scoped lang="scss">
/* ============================================================
   整体：高度改为 100% 以适配后台框架
   ============================================================ */
.shipping-dashboard {
  height: 100%;
  /* 占满父容器 */
  min-height: calc(100vh - 84px);
  /* 兜底高度，适配多数后台带顶栏的结构 */
  box-sizing: border-box;
  padding: 14px 16px;
  display: flex;
  flex-direction: column;
  gap: 14px;
  overflow: hidden;
  background: #f4f6fb;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto,
    'Helvetica Neue', Arial, sans-serif;
}

.top-row {
  flex: 1.15 1 0;
  display: flex;
  gap: 14px;
  min-height: 0;
  height: 100%;
}

.rank-row {
  flex: 1 1 0;
  display: flex;
  gap: 14px;
  min-height: 0;
  height: 100%;
}

/* 卡片 */
.panel {
  flex: 1 1 0;
  min-width: 0;
  min-height: 0;
  display: flex;
  flex-direction: column;
  padding: 14px 18px;
  background: #ffffff;
  border: 1px solid #eef2f7;
  border-radius: 14px;
  box-shadow: 0 2px 10px rgba(15, 41, 77, 0.05);
  overflow: hidden;
  transition: box-shadow 0.25s ease;
}

.panel:hover {
  box-shadow: 0 8px 22px rgba(15, 41, 77, 0.09);
}

.panel-header {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 12px;
}

.panel-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 15px;
  font-weight: 600;
  color: #1e293b;
  white-space: nowrap;
}

.title-dot {
  width: 4px;
  height: 15px;
  border-radius: 2px;
  background: linear-gradient(180deg, #7c9df5 0%, #5470c6 100%);
}

.dot-warning {
  background: linear-gradient(180deg, #ffb36b 0%, #f5222d 100%);
}

.dot-order {
  background: linear-gradient(180deg, #7c9df5 0%, #5470c6 100%);
}

.dot-sales {
  background: linear-gradient(180deg, #7c9df5 0%, #5470c6 100%);
}

.dot-click {
  background: linear-gradient(180deg, #7ee0b0 0%, #13a86a 100%);
}

.panel-tools {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: nowrap;
}

/* 卡片内容区 */
.panel-body {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  position: relative;
}

.panel-body--bordered {
  overflow-x: auto;
  border: 1px solid #eef2f6;
  border-radius: 10px;
  background: #fff;
}

.panel-footer {
  flex-shrink: 0;
  margin-top: 10px;
}

/* ============================================================
   表格通用样式（原生 table）
   ============================================================ */
.data-table {
  width: 100%;
  min-width: 420px;
  border-collapse: collapse;
  font-size: 13px;
  table-layout: fixed;
}

.data-table thead tr th {
  position: sticky;
  top: 0;
  z-index: 10;
  padding: 11px 14px;
  text-align: left;
  font-weight: 600;
  font-size: 13px;
  color: #1e293b;
  letter-spacing: 0.2px;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  border-bottom: 1px solid #e2e8f0;
  white-space: nowrap;
}

.data-table td {
  padding: 10px 14px;
  color: #334155;
  font-weight: 450;
  border-bottom: 1px solid #f2f5f9;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  transition: background-color 0.2s ease;
}

.data-table tbody tr:hover td {
  background-color: #f7faff;
}

.data-table tbody tr:last-child td {
  border-bottom: none;
}

.name-cell {
  color: #1f2d3d;
  font-weight: 500;
}

.name-cell :hover {
  text-decoration: underline;
  /* 鼠标移上去出现下划线 */
}

.time-cell {
  color: #7b8794;
  font-family: 'SF Mono', 'Menlo', monospace;
  font-size: 12px;
}

.user-cell {
  font-weight: 500;
  color: #0f172a;
}

.month-orders {
  font-weight: 600;
  color: #5470c6;
}

.empty-cell {
  text-align: center !important;
  padding: 40px 16px !important;
  color: #a8b4c6;
  font-size: 13px;
}

.note-text {
  display: inline-block;
  padding: 5px 10px;
  font-size: 12px;
  color: #8a99b4;
  background: #f7f9fc;
  border-radius: 6px;
}

/* ============================================================
   Element 控件紧凑化
   ============================================================ */
::v-deep .el-input__inner {
  height: 30px;
  line-height: 30px;
  font-size: 13px;
  border-radius: 8px;
  border-color: #e6ebf2;
  background: #f7f9fc;
  color: #334155;
}

::v-deep .el-input__icon {
  line-height: 30px;
}

::v-deep .el-input__inner:hover {
  border-color: #c7d5ea;
}

::v-deep .el-input.is-focus .el-input__inner {
  border-color: #5470c6;
  background: #fff;
}

::v-deep .el-tag {
  border-radius: 6px;
}

/* 导出按钮 */
.export-btn {
  display: flex;
  align-items: center;
  gap: 5px;
  height: 30px;
  padding: 0 14px;
  border: none;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 500;
  color: #fff;
  background: linear-gradient(135deg, #6b86d6 0%, #5470c6 100%);
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 2px 6px rgba(84, 112, 198, 0.25);
}

.export-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 5px 12px rgba(84, 112, 198, 0.35);
}

.export-btn:active {
  transform: translateY(0);
}

.export-icon {
  display: inline-block;
  width: 14px;
  height: 14px;
  background: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='white'%3E%3Cpath d='M19 9h-4V3H9v6H5l7 7 7-7zM5 18v2h14v-2H5z'/%3E%3C/svg%3E") no-repeat center;
  background-size: contain;
}

/* ============================================================
   排行榜
   ============================================================ */
.rank-subtitle {
  padding: 2px 9px;
  font-size: 11px;
  font-weight: 500;
  color: #8a99b4;
  background: #f1f5f9;
  border-radius: 20px;
}

.rank-list {
  padding-right: 4px;
}

.rank-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 8px;
  border-radius: 10px;
  transition: background-color 0.2s ease;
}

.rank-item:hover {
  background-color: #f7faff;
}

.rank-index {
  flex-shrink: 0;
  width: 22px;
  height: 22px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 600;
  color: #94a3b8;
  background: #f1f5f9;
  border-radius: 7px;
}

.rank-1 {
  background: linear-gradient(135deg, #ffd666 0%, #fa8c16 100%);
  color: #fff;
}

.rank-2 {
  background: linear-gradient(135deg, #d6e4f0 0%, #8fa6bc 100%);
  color: #fff;
}

.rank-3 {
  background: linear-gradient(135deg, #ffd8bf 0%, #d46b08 100%);
  color: #fff;
}

.rank-img {
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  border-radius: 8px;
  object-fit: cover;
  background: #f7f8fc;
  border: 1px solid #eef2f6;
}

.rank-info {
  flex: 1;
  min-width: 0;
}

.rank-name {
  margin-bottom: 5px;
  font-size: 13px;
  font-weight: 500;
  color: #1f2d3d;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.rank-bar {
  height: 5px;
  background: #f1f5f9;
  border-radius: 6px;
  overflow: hidden;
}

.rank-bar-inner {
  height: 100%;
  border-radius: 6px;
  background: linear-gradient(90deg, #7c9df5 0%, #5470c6 100%);
  transition: width 0.4s ease;
}

.rank-bar-click {
  background: linear-gradient(90deg, #7ee0b0 0%, #13a86a 100%);
}

.rank-sales {
  flex-shrink: 0;
  min-width: 52px;
  text-align: right;
  font-size: 16px;
  font-weight: 600;
  color: #5470c6;
}

.rank-sales-click {
  color: #13a86a;
}

.rank-unit {
  margin-left: 2px;
  font-size: 11px;
  font-weight: 400;
  color: #8a99b4;
}

/* ============================================================
   滚动条美化
   ============================================================ */
.panel-body::-webkit-scrollbar {
  width: 7px;
  height: 7px;
}

.panel-body::-webkit-scrollbar-track {
  background: transparent;
}

.panel-body::-webkit-scrollbar-thumb {
  background: #d3dce8;
  border-radius: 10px;
}

.panel-body::-webkit-scrollbar-thumb:hover {
  background: #b4c1d4;
}

/* 紧急标签闪烁 */
@keyframes gentle-blink {
  0% {
    opacity: 1;
    box-shadow: 0 0 0 rgba(245, 34, 45, 0.35);
  }

  50% {
    opacity: 0.65;
    box-shadow: 0 0 6px rgba(245, 34, 45, 0.55);
  }

  100% {
    opacity: 1;
    box-shadow: 0 0 0 rgba(245, 34, 45, 0.35);
  }
}

.blink-tag {
  animation: gentle-blink 1.2s ease-in-out infinite;
}

/* ============================================================
   响应式：小屏时允许页面纵向滚动，卡片改为上下排列
   ============================================================ */
@media (max-width: 1100px) {
  .shipping-dashboard {
    height: auto;
    min-height: 100%;
    overflow: visible;
  }

  .top-row,
  .rank-row {
    flex: none;
    flex-direction: column;
  }

  .panel {
    height: 380px;
  }
}

@media (max-width: 768px) {
  .shipping-dashboard {
    padding: 10px;
    gap: 10px;
  }

  .panel {
    padding: 12px 12px;
    border-radius: 12px;
  }

  .panel-header {
    flex-wrap: wrap;
  }

  .data-table td,
  .data-table thead tr th {
    padding: 9px 10px;
  }
}
</style>